import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import ContextNav from './navigation/ContextNav';
import CommandBar from './command/CommandBar';
import AIChatPanel from './AIChatPanel';

export default function ModernLayout() {
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 transition-colors duration-200">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-violet-600 focus:text-white focus:rounded-lg focus:outline-none"
      >
        Skip to content
      </a>
      {/* Liquid glass background - vibrant ambient lighting */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" />

        {/* Primary accent - violet (top left) */}
        <div className="absolute -top-20 -left-20 w-[700px] h-[700px] bg-violet-400/40 dark:bg-violet-500/30 rounded-full blur-[120px] animate-pulse-slow" />

        {/* Secondary accent - blue (bottom right) */}
        <div className="absolute -bottom-32 -right-20 w-[600px] h-[600px] bg-blue-400/40 dark:bg-blue-500/30 rounded-full blur-[100px] animate-pulse-slow animation-delay-1000" />

        {/* Tertiary accent - purple (center right) */}
        <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-violet-400/35 dark:bg-violet-500/25 rounded-full blur-[80px] animate-pulse-slow animation-delay-2000" />

        {/* Quaternary accent - cyan (bottom left) */}
        <div className="absolute bottom-1/4 -left-20 w-[450px] h-[450px] bg-cyan-400/35 dark:bg-cyan-500/25 rounded-full blur-[90px] animate-pulse-slow animation-delay-3000" />

        {/* Warm accent - amber (top center) */}
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-amber-300/25 dark:bg-amber-500/15 rounded-full blur-[70px]" />

        {/* Subtle noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      {/* Command Bar (global) */}
      <CommandBar />

      {/* Navigation */}
      <nav aria-label="Main navigation">
        <ContextNav />
      </nav>

      {/* Main Content */}
      <motion.main
        id="main-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="relative pt-32 pb-16"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
          <div className="space-y-6">
            <Outlet />
          </div>
        </div>
      </motion.main>

      {/* Floating Action Button */}
      <FloatingActions />

      {/* AI Chat Panel */}
      <AIChatPanel />
    </div>
  );
}

function FloatingActions() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5 }}
      className="fixed bottom-6 right-24 z-30"
    >
      <button
        onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
        aria-label="Open command bar"
        className="w-14 h-14 bg-gradient-to-br from-violet-400 to-violet-600 rounded-full shadow-lg shadow-violet-500/30 flex items-center justify-center text-white hover:scale-110 transition-transform"
      >
        <span className="text-2xl">⌘</span>
      </button>
    </motion.div>
  );
}
